import { useCallback, useEffect, useMemo, useState } from 'react';

export const OPENED_TASKS_KEY_PREFIX = 'opened-tasks';
export const MAX_OPENED_TASKS = 300;

// Per user, so two users sharing a browser each see their own reading.
export const openedTasksStorageKey = (userId) => `${OPENED_TASKS_KEY_PREFIX}:${userId}`;

// Storage can be missing, full, blocked or hold anything: reads fall back to
// an empty list and writes are best effort.
const readIds = (storageKey) => {
  try {
    const ids = JSON.parse(window.localStorage.getItem(storageKey));
    return Array.isArray(ids) ? ids.map(String) : [];
  } catch (error) {
    return [];
  }
};

// The panel marks a task and the lists read the marks: every mounted hook is
// told, so a list re-reads what a panel opened elsewhere in the app wrote.
const listeners = new Set();
const tellListeners = (storageKey) => listeners.forEach((listener) => listener(storageKey));

/**
 * The tasks whose panel the user opened, in this browser: ids under a key per
 * user, the most recently opened last, the oldest dropped beyond
 * MAX_OPENED_TASKS. Without a user, nothing is read or kept.
 */
const useOpenedTasks = (userId) => {
  const storageKey = userId ? openedTasksStorageKey(userId) : null;
  // Bumped on each mark: a dependency of the read, so the ids are read again.
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (!storageKey) return undefined;
    const listener = (markedKey) => {
      if (markedKey === storageKey) setVersion((current) => current + 1);
    };
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, [storageKey]);
  const openedIds = useMemo(
    () => new Set(storageKey ? readIds(storageKey) : []),
    [storageKey, version],
  );
  const isOpened = useCallback((taskId) => taskId != null && openedIds.has(String(taskId)), [openedIds]);
  const markOpened = useCallback(
    (taskId) => {
      if (!storageKey || taskId == null) return;
      const id = String(taskId);
      const ids = [...readIds(storageKey).filter((kept) => kept !== id), id].slice(-MAX_OPENED_TASKS);
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(ids));
      } catch (error) {
        // Nothing to do: the task simply stays highlighted.
      }
      tellListeners(storageKey);
    },
    [storageKey],
  );
  return { isOpened, markOpened };
};

export default useOpenedTasks;
